// Form controls: Field wrapper, Input, Select, Checkbox, Toggle, Slider, Segmented. All are plain
// native controls under the hood (keyboard and screen readers work for free, §14).
import { useId, type CSSProperties, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

export function Field({ label, hint, row, grow, htmlFor, children, className = '', style }: { label?: ReactNode; hint?: ReactNode; row?: boolean; grow?: boolean; htmlFor?: string; children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={`fk-field ${row ? 'fk-field--row' : ''} ${grow ? 'fk-field--grow' : ''} ${className}`} style={style}>
      {label && <label className="fk-field__label" htmlFor={htmlFor}>{label}</label>}
      {children}
      {hint && <div className="fk-field__hint">{hint}</div>}
    </div>
  );
}

export function Input({ small, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { small?: boolean }) {
  return <input className={`fk-input ${small ? 'fk-input--sm' : ''} ${className}`} {...rest} />;
}

export function Select({ className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`fk-select ${className}`} {...rest}>{children}</select>;
}

export function Checkbox({ label, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={`fk-check ${className}`}>
      <input type="checkbox" {...rest} />
      <span className="fk-check__box" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

export function Toggle({ label, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={`fk-toggle ${className}`}>
      <input type="checkbox" role="switch" aria-checked={rest.checked} {...rest} />
      <span className="fk-toggle__track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

export function Slider({ value, min = 0, max = 100, step = 1, onChange, format = (v: number) => String(v), label, className = '', disabled }: {
  value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void; format?: (v: number) => string; label: string; className?: string; disabled?: boolean;
}) {
  const id = useId();
  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;
  return (
    <div className={`fk-slider ${className}`}>
      <input id={id} type="range" min={min} max={max} step={step} value={value} disabled={disabled} aria-label={label} aria-valuetext={format(value)}
        style={{ ['--fk-slider-pct' as string]: `${pct}%` }} onChange={(e) => onChange(Number(e.target.value))} />
      <output htmlFor={id} className="fk-slider__value">{format(value)}</output>
    </div>
  );
}

export function Segmented<T extends string | number>({ options, value, onChange, label, className = '', disabled }: {
  options: ReadonlyArray<{ value: T; label: ReactNode; title?: string; disabled?: boolean }>; value: T; onChange: (v: T) => void; label: string; className?: string; disabled?: boolean;
}) {
  return (
    <div className={`fk-segmented ${className}`} role="group" aria-label={label}>
      {options.map((o) => <button key={String(o.value)} type="button" className="fk-segmented__opt" aria-pressed={o.value === value} title={o.title} disabled={disabled || o.disabled} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}
